import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useHasRole } from '@/hooks/useHasRole';
import { useAdminDashboard, useSyncStripeSubscriptions, type AdminDashboardData } from '@/hooks/useAdminDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Users, UserCircle, HeartPulse, Eye, Bell, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw, DollarSign, Crown, ClipboardList, CalendarClock, Handshake, Settings, Clock,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AppLayout } from '@/components/layout/AppLayout';
import { Area, AreaChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ReferralsAdminContent } from './AdminReferralsPage';
import { AdminUsersContent } from './AdminUsers';

const PERIODS = [7, 30, 90] as const;

function formatCurrencyCents(cents: number, currency = 'brl') {
  const value = (cents || 0) / 100;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: (currency || 'BRL').toUpperCase() });
}

function KPICard({ icon: Icon, value, label, tone = 'primary' }: any) {
  const toneClasses: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
    muted: 'bg-muted text-muted-foreground',
  };
  return (
    <Card>
      <CardContent className="p-3 sm:p-4 flex items-start gap-3">
        <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${toneClasses[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xl font-bold leading-tight break-words sm:text-lg">{value}</p>
          <p className="text-xs text-muted-foreground leading-snug break-words">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function DeltaBadge({ delta }: { delta: number }) {
  const isUp = delta >= 0;
  const Icon = isUp ? TrendingUp : TrendingDown;
  const cls = isUp ? 'text-success' : 'text-destructive';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

export default function AdminDashboardPage() {
  const isAdmin = useHasRole('careos_admin');
  const [period, setPeriod] = useState<number>(30);
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);
  const { data, isLoading, isError, refetch, isRefetching } = useAdminDashboard(period);
  const sync = useSyncStripeSubscriptions();


  if (isAdmin === null) {
    return (
      <AppLayout title="Painel administrativo">
        <div className="min-h-[200px] flex items-center justify-center">
          <Skeleton className="h-12 w-48" />
        </div>
      </AppLayout>
    );
  }
  if (isAdmin === false) return <Navigate to="/app" replace />;

  return (
    <AppLayout title="Painel administrativo">
      <div className="container mx-auto p-4 space-y-5 max-w-7xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 items-center flex-wrap">
          {PERIODS.map((p) => (
            <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} onClick={() => setPeriod(p)}>
              {p} dias
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => { refetch(); setUsersRefreshKey((k) => k + 1); }} disabled={isRefetching}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/admin/stripe">
              <Settings className="h-4 w-4 mr-1" />
              Configuração Stripe
            </Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/admin/cron">
              <Clock className="h-4 w-4 mr-1" />
              Jobs agendados
            </Link>
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">
            Erro ao carregar o painel. Verifique se você tem o papel <code>careos_admin</code> e tente novamente.
          </CardContent>
        </Card>
      )}

      {data && (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview">Visão geral</TabsTrigger>
            <TabsTrigger value="adoption">Adoção</TabsTrigger>
            <TabsTrigger value="engagement">Engajamento</TabsTrigger>
            <TabsTrigger value="notifications">Notificações</TabsTrigger>
            <TabsTrigger value="revenue">Receita</TabsTrigger>
            <TabsTrigger value="users">
              <UserCircle className="h-4 w-4 mr-1" />Usuários
            </TabsTrigger>
            <TabsTrigger value="referrals">
              <Handshake className="h-4 w-4 mr-1" />Indicações
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPICard icon={Building2} value={data.volume.active_companies} label="Empresas ativas" />
              <KPICard icon={Users} value={data.volume.active_caregivers} label="Cuidadores ativos" />
              <KPICard icon={UserCircle} value={data.volume.active_family_owners} label="Famílias ativas" />
              <KPICard icon={HeartPulse} value={data.volume.total_patients} label="Pacientes" />
              <KPICard icon={Eye} value={data.volume.active_observers} label="Observadores" tone="muted" />
              <KPICard icon={Building2} value={data.volume.patients_company_linked} label="Pacientes em empresas" tone="muted" />
              <KPICard icon={UserCircle} value={data.volume.patients_family_only} label="Pacientes familiares" tone="muted" />
              <KPICard icon={CalendarClock} value={`${data.lifetime.avg_account_age_days}d`} label="Idade média conta" tone="muted" />
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base">Distribuição de tempo de vida (famílias)</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="p-3 rounded bg-muted"><p className="font-bold text-lg">{data.lifetime.buckets.lt30}</p><p className="text-muted-foreground">menos de 30 dias</p></div>
                  <div className="p-3 rounded bg-muted"><p className="font-bold text-lg">{data.lifetime.buckets['30_90']}</p><p className="text-muted-foreground">30 a 90 dias</p></div>
                  <div className="p-3 rounded bg-muted"><p className="font-bold text-lg">{data.lifetime.buckets['90_180']}</p><p className="text-muted-foreground">90 a 180 dias</p></div>
                  <div className="p-3 rounded bg-muted"><p className="font-bold text-lg">{data.lifetime.buckets.gt180}</p><p className="text-muted-foreground">mais de 180 dias</p></div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="adoption" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPICard icon={ClipboardList} value={data.adoption.without_plan} label="Sem plano de cuidado" tone="warning" />
              <KPICard icon={AlertTriangle} value={`${data.adoption.without_plan_pct}%`} label="% sem plano" tone="warning" />
              <KPICard icon={CalendarClock} value={data.adoption.without_template} label="Sem escala" tone="warning" />
              <KPICard icon={AlertTriangle} value={`${data.adoption.without_template_pct}%`} label="% sem template" tone="warning" />
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base">Pacientes sem plano de cuidado ({data.adoption.without_plan})</CardTitle></CardHeader>
              <CardContent>
                <DrillTable rows={data.adoption.without_plan_list} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Pacientes sem escala ({data.adoption.without_template})</CardTitle></CardHeader>
              <CardContent>
                <DrillTable rows={data.adoption.without_template_list} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="engagement" className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <KPICard icon={Users} value={data.engagement.dau} label="DAU (24h)" />
              <KPICard icon={Users} value={data.engagement.wau} label="WAU (7d)" />
              <KPICard icon={Users} value={data.engagement.mau} label="MAU (30d)" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.engagement.features.map((f) => (
                <FeatureTrendCard key={f.key} feature={f} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPICard icon={Bell} value={data.notifications.in_app_total} label={`Notificações in-app (${period}d)`} />
              <KPICard icon={CheckCircle2} value={data.notifications.push_ok} label="Pushs entregues" tone="success" />
              <KPICard icon={XCircle} value={data.notifications.push_failed} label="Pushs falhados" tone="destructive" />
              <KPICard
                icon={CheckCircle2}
                value={data.notifications.push_success_rate === null ? '—' : `${data.notifications.push_success_rate}%`}
                label="Taxa de sucesso push"
                tone={data.notifications.push_success_rate === null ? 'muted' : (data.notifications.push_success_rate >= 90 ? 'success' : 'warning')}
              />
            </div>
            <Card>
              <CardContent className="p-4 text-sm flex items-center justify-between">
                <span>Comparativo de notificações in-app vs período anterior:</span>
                <DeltaBadge delta={data.notifications.delta_pct} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revenue" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPICard icon={Crown} value={data.revenue.active_subscriptions} label="Assinaturas ativas" tone="success" />
              <KPICard
                icon={DollarSign}
                value={formatCurrencyCents(data.revenue.mrr_cents, data.revenue.active_subscriptions_list[0]?.currency ?? 'brl')}
                label="MRR estimado"
                tone="success"
              />
              <KPICard icon={XCircle} value={data.revenue.churned_in_period} label={`Churn (${period}d)`} tone="destructive" />
              <KPICard icon={AlertTriangle} value={data.revenue.free_overflow_count} label="Free acima do limite" tone="warning" />
            </div>

            <div className="flex gap-2 items-center flex-wrap">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => sync.mutate({ mode: 'incremental' }, {
                  onSuccess: (r) => toast.success(`Reconciliação incremental: ${r?.upserts ?? 0} assinaturas atualizadas`),
                  onError: (e: any) => toast.error(e?.message ?? 'Falha na reconciliação'),
                })}
                disabled={sync.isPending}
                title="Lê apenas os eventos recentes do Stripe (últimas ~26h por padrão). Rápido e escalável."
              >
                <DollarSign className={`h-4 w-4 mr-1 ${sync.isPending ? 'animate-pulse' : ''}`} />
                {sync.isPending ? 'Reconciliando…' : 'Reconciliar (incremental)'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!confirm('Backfill completo varre TODAS as assinaturas do Stripe. Use só ocasionalmente. Continuar?')) return;
                  sync.mutate({ mode: 'full' }, {
                    onSuccess: (r) => toast.success(`Backfill concluído: ${r?.upserts ?? 0} assinaturas`),
                    onError: (e: any) => toast.error(e?.message ?? 'Falha no backfill'),
                  });
                }}
                disabled={sync.isPending}
                title="Varre todas as assinaturas do Stripe. Use apenas para backfill — não escala com milhares de clientes."
              >
                Backfill completo
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Donos no plano free que ultrapassaram 10 plantões ({data.revenue.free_overflow_count})</CardTitle>
              </CardHeader>
              <CardContent>
                <FreeOwnersTable rows={data.revenue.free_overflow_list} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Donos no plano free dentro do limite ({data.revenue.free_within_count})</CardTitle>
              </CardHeader>
              <CardContent>
                <FreeOwnersTable rows={data.revenue.free_within_list} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Assinaturas ativas ({data.revenue.active_subscriptions_list.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead>Próx. cobrança</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.revenue.active_subscriptions_list.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="truncate max-w-[200px]">{s.email}</TableCell>
                        <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          {s.unit_amount != null ? formatCurrencyCents(s.unit_amount, s.currency ?? 'brl') : '—'}
                          {s.interval ? <span className="text-xs text-muted-foreground"> /{s.interval}</span> : null}
                        </TableCell>
                        <TableCell className="text-right">{s.quantity ?? '—'}</TableCell>
                        <TableCell>
                          {s.current_period_end ? format(new Date(s.current_period_end), 'dd/MM/yyyy', { locale: ptBR }) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.revenue.active_subscriptions_list.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Nenhuma assinatura ativa. Clique em "Reconciliar com Stripe (fallback)".</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <AdminUsersContent periodDays={period} refreshKey={usersRefreshKey} />
          </TabsContent>

          <TabsContent value="referrals" className="space-y-4">
            <ReferralsAdminContent />
          </TabsContent>
        </Tabs>
      )}

      {data && (
        <p className="text-xs text-muted-foreground text-center">
          Atualizado em {format(new Date(data.generated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
        </p>
      )}
      </div>
    </AppLayout>
  );
}

function labelFeature(k: string) {
  const map: Record<string, string> = {
    checkins: 'Inícios de plantão',
    task_executions: 'Execuções de tarefa',
    incidents: 'Incidentes registrados',
    sporadic_meds: 'Medicações esporádicas',
    comments: 'Comentários',
  };
  return map[k] ?? k;
}

function DrillTable({ rows }: { rows: Array<{ patient_id: string; nickname: string; owner_email: string; created_at: string }> }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground">Nenhum registro.</p>;
  return (
    <div className="max-h-96 overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Paciente</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Cadastrado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.patient_id}>
              <TableCell className="font-medium">
                <a href={`/app/patients/${r.patient_id}`} className="hover:underline">{r.nickname}</a>
              </TableCell>
              <TableCell className="truncate max-w-[200px]">{r.owner_email || '—'}</TableCell>
              <TableCell>{format(new Date(r.created_at), 'dd/MM/yyyy', { locale: ptBR })}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function FeatureTrendCard({ feature }: { feature: AdminDashboardData['engagement']['features'][number] }) {
  const series = feature.series ?? [];
  const total = feature.current;
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm font-medium break-words">{labelFeature(feature.key)}</CardTitle>
          <DeltaBadge delta={feature.delta_pct} />
        </div>
        <p className="text-2xl font-bold leading-tight">{total.toLocaleString('pt-BR')}</p>
        <p className="text-xs text-muted-foreground">vs anterior: {feature.previous.toLocaleString('pt-BR')}</p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`grad-${feature.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(d) => format(parseISO(d), 'dd/MM')}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                allowDecimals={false}
                width={28}
              />
              <RTooltip
                contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', fontSize: 12 }}
                labelFormatter={(d: string) => format(parseISO(d), "dd 'de' MMM", { locale: ptBR })}
                formatter={(v: number) => [v, labelFeature(feature.key)]}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill={`url(#grad-${feature.key})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function FreeOwnersTable({ rows }: { rows: Array<{ user_id: string; email: string; shifts: number }> }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground">Nenhum registro.</p>;
  return (
    <div className="max-h-96 overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>E-mail</TableHead>
            <TableHead className="text-right">Plantões</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.user_id}>
              <TableCell className="truncate max-w-[300px]">{r.email || r.user_id}</TableCell>
              <TableCell className="text-right font-medium">{r.shifts}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
